import type { Proxy } from '#~/agent/index.js';
import { Input } from '@studio/design-system/input';
import * as Slider from '@studio/design-system/slider';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Control, DeviceRegistryCommand } from '@tryiris-ai/common.protojs/agent/v1/agent';
import {
  DeviceRegistryCommandFunctionType,
  DeviceRegistryCommandKey,
  DeviceRegistryInterfaceItemControlType
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { twMerge } from 'cn';
import type { SettingsStatus } from './status-notifications.js';
import { STATUS_NOTIFICATIONS } from './status-notifications.js';
import type { Device } from '@studio/models/device';

interface DeviceSettingsDirectNumericEntryProps {
  agent?: T_Maybe<Proxy>;
  className: string;
  cmd: DeviceRegistryCommand;
  cmdKey: DeviceRegistryCommandKey;
  controlType: DeviceRegistryInterfaceItemControlType;
  defaultValue: T_Maybe<number>;
  deviceId: Device['id'];
  disabled?: boolean;
  label: string;
  onMutation: T_Maybe<(mutation: DeepPartial<Control>) => void>;
  value: number;
  onControlClick?: (controlData: {
    label: string;
    controlType: DeviceRegistryInterfaceItemControlType;
    key: DeviceRegistryCommandKey;
    value?: DeviceRegistryCommandFunctionType;
  }) => void;
  status?: SettingsStatus;
}

/** `<DeviceSettingSlider>` component. */
export const DeviceSettingDirectNumericEntry = ({
  agent,
  className,
  cmd,
  cmdKey,
  defaultValue,
  deviceId,
  disabled,
  label,
  onMutation,
  value,
  onControlClick,
  status
}: DeviceSettingsDirectNumericEntryProps): ReactElement => {
  const [displayValue, setDisplayValue] = useState<number | undefined>(undefined);
  const [interfaceTimeout, setInterfaceTimeout] = useState<NodeJS.Timeout | undefined>(undefined);

  const { max: humanMax, min: humanMin } = cmd.value?.rangeValue ?? { min: 0, max: 1 };

  const humanValue = (humanMax - humanMin) * value + humanMin;
  const safeValue = displayValue ?? humanValue;

  const valid = safeValue >= humanMin && safeValue <= humanMax;

  const defaultValueHuman = (humanMax - humanMin) * (defaultValue ?? 0) + humanMin;

  const mutateValue = (nextValue: number): void => {
    clearTimeout(interfaceTimeout);

    const saveTimeout = setTimeout(async () => {
      if (nextValue < 0 || nextValue > 1) {
        return;
      }

      const control = {
        device: {
          [deviceId]: {
            deviceSettings: {
              [String(cmdKey)]: {
                direct: nextValue,
                key: cmdKey,
                lastModified: Date.now()
              }
            }
          }
        }
      };

      if (onMutation) {
        onMutation(control);
        onControlClick?.({
          label,
          controlType: DeviceRegistryInterfaceItemControlType.DIRECT_NUMERIC_ENTRY,
          key: cmdKey,
          value: DeviceRegistryCommandFunctionType.VALUE_SET
        });

        return;
      }

      await agent?.mutate(
        {
          control
        },
        {}
      );

      onControlClick?.({
        label,
        controlType: DeviceRegistryInterfaceItemControlType.DIRECT_NUMERIC_ENTRY,
        key: cmdKey,
        value: DeviceRegistryCommandFunctionType.VALUE_SET
      });

      setDisplayValue(undefined);
    }, 1000);

    setInterfaceTimeout(saveTimeout);
  };

  return (
    <Slider.Container
      canRevert={!disabled && defaultValue !== undefined && safeValue !== defaultValueHuman}
      className={twMerge(className, !valid && 'text-error')}
      label={label}
      status={status}
      notification={status ? STATUS_NOTIFICATIONS[status] : undefined}
      onRevert={(): void => {
        setDisplayValue(defaultValueHuman);

        mutateValue(defaultValue!);
      }}
    >
      <Input
        className='mt-4'
        disabled={disabled}
        onChange={(nextValue): void => {
          const numericValue = Number(nextValue);

          if (Number.isNaN(numericValue)) {
            return;
          }

          setDisplayValue(numericValue);

          const scaledValue = (numericValue - humanMin) / (humanMax - humanMin);

          mutateValue(scaledValue);
        }}
        value={safeValue}
      />
    </Slider.Container>
  );
};
